import { useMemo, useState } from 'react';

import { useConfirm } from '@contexts/Confirm.context';
import {
	DEFAULT_PRESET,
	DEFAULT_SIMPLE_PRESET,
} from '@routes/userRoutes/projects/singleProject/components/modifyLayers/hexagonColorFilter/matrixFilter/matrixClassification';
import useMatrixTemplates from '@routes/userRoutes/projects/singleProject/components/modifyLayers/hexagonColorFilter/matrixFilter/useMatrixTemplates';

const clone = preset => JSON.parse(JSON.stringify(preset));

/**
 * Filter-mode selection + template CRUD for the hexagon color-filter: mode
 * (matrix/simple) initialization and switching, template load/save/delete,
 * and the template-select dropdown's options.
 *
 * The dropdown lists rather than omits: templates referencing a property the
 * current collection doesn't have stay listed and disabled, so a template
 * someone expects to find is still findable. Their option label is the plain
 * template name and the disabled state is the only signal — the picker carries
 * no annotation on the label and no warning below the field.
 *
 * Isolated from classification/export concerns — this hook only ever reads or
 * writes the filter config it is handed, never the derived stats/results. It is
 * therefore shared by the hexagon and DSP color-filters, which back their config
 * with different store fields.
 *
 * @param {object|null} filter - active filter config (store value)
 * @param {(next: object) => void} setFilter
 * @param {number} resolution - hexagon cell size, passed to useMatrixTemplates as
 *   hexagonSize. Pass 0 for DSP, where plot size is the image footprint rather
 *   than a caller choice and the saved value is meaningless.
 * @param {Set<string>} availableIds - property ids present in this collection's data
 */
const useHexagonFilterTemplates = (filter, setFilter, resolution, availableIds) => {
	const { confirm } = useConfirm();

	const [activeTemplate, setActiveTemplate] = useState(null);
	const [showSaveModal, setShowSaveModal] = useState(false);
	const [saveTitle, setSaveTitle] = useState('');

	// No default is seeded here: this hook's owner stays mounted while its sidebar
	// is closed, so a mount effect would color the map before the panel was ever
	// opened. Seeding belongs to whoever opens it (the layers' `openColorFilter`).
	const isSimple = filter?.type === 'simple';

	const { matrixTemplates, saveTemplate, saveMutation, deleteMutation, loadTemplate } =
		useMatrixTemplates({
			activeTemplate,
			setActiveTemplate,
			setFilter,
			setShowSaveModal,
			hexagonSize: resolution,
			filter: filter ?? {},
		});

	// Selecting a mode (re)initializes from that mode's default preset — matches
	// the model's behavior and guarantees a valid config for the chosen type.
	const selectMode = mode => {
		setActiveTemplate(null);
		setFilter(clone(mode === 'simple' ? DEFAULT_SIMPLE_PRESET : DEFAULT_PRESET));
	};

	// Templates for the current mode. A template referencing a property this
	// collection doesn't have is disabled rather than hidden, because it would
	// classify nothing yet should still be findable — and disabled is the whole
	// signal: its label stays the untouched template name, since unavailability is
	// a fact about this collection and the same template is fine in another.
	const templateOptions = useMemo(() => {
		return matrixTemplates
			.filter(t =>
				isSimple ? t.matrixConfig?.type === 'simple' : t.matrixConfig?.type === 'matrix'
			)
			.map(t => {
				const cfg = t.matrixConfig;
				const refProps =
					cfg.type === 'simple'
						? [cfg.property]
						: [cfg.bandProperty, cfg.thresholdProperty];
				const missing = refProps.filter(p => p && !availableIds.has(p));
				return {
					value: t.uuid,
					label: t.title,
					disabled: missing.length > 0,
				};
			});
	}, [matrixTemplates, isSimple, availableIds]);

	const handleTemplateSelect = uuid => {
		const template = matrixTemplates.find(t => t.uuid === uuid);
		if (template) loadTemplate(template);
	};

	// Confirm before deleting — mirrors the model path's FilterTemplates.jsx,
	// which also gates deletion behind useConfirm rather than deleting on click.
	const handleDeleteTemplate = async () => {
		// Captured before the await — the user can clear/change the template
		// selection while the confirm dialog is open, which would otherwise
		// delete a different template (or throw on a null activeTemplate).
		const uuid = activeTemplate?.uuid;
		if (!uuid) return;
		const userConfirmed = await confirm({
			title: 'Delete Filter Template',
			message: (
				<>
					Are you sure you want to delete the filter template{' '}
					<strong>{activeTemplate?.title}</strong>?
				</>
			),
		});
		if (userConfirmed) deleteMutation.mutate(uuid);
	};

	return {
		activeTemplate,
		setActiveTemplate,
		showSaveModal,
		setShowSaveModal,
		saveTitle,
		setSaveTitle,
		saveTemplate,
		saveMutation,
		deleteMutation,
		templateOptions,
		selectMode,
		handleTemplateSelect,
		handleDeleteTemplate,
	};
};

export default useHexagonFilterTemplates;
